/**
 * The jurisdictions of a started report, with a way to drop one.
 *
 * Once a report has a Backoffice project its jurisdictions are tags there, and
 * the project is only ever added to — so a jurisdiction the client drops has to
 * be removed explicitly. Each pill has a remove button that asks for a reason
 * first. The report page gives us jurisdiction names; a name that can't be
 * matched to a code is shown but can't be removed from here.
 */

import { useState } from 'react';

import { ActionIcon, Group, Text } from '@mantine/core';
import { IconX } from '@tabler/icons-react';

import { BackofficeBehindNotice } from '@/components/BackofficeBehindNotice';
import { CountryPill } from '@/components/CountryPill';
import { JURISDICTIONS } from '@/constants';
import { useReportJurisdictions } from '@/hooks/useReportJurisdictions';
import type { BackofficeSyncMarker } from '@/types/backoffice-sync';
import type { ReportResponse } from '@/types/report';
import { RemoveJurisdictionDialog } from './RemoveJurisdictionDialog';

interface ReportJurisdictionsProps {
  reportId: string;
  /** Jurisdiction names across the report's subjects. */
  jurisdictions: readonly string[];
  /** Persisted "Backoffice is behind" state of the report, if any. */
  syncMarker?: BackofficeSyncMarker | null;
  onUpdated: (updated: ReportResponse) => void;
}

export function ReportJurisdictions({
  reportId,
  jurisdictions,
  syncMarker = null,
  onUpdated,
}: Readonly<ReportJurisdictionsProps>) {
  const { removeJurisdictions } = useReportJurisdictions();
  const [removing, setRemoving] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (jurisdictions.length === 0) {
    return null;
  }

  const confirm = async (reason: string) => {
    const code = JURISDICTIONS.find((c) => c.name === removing)?.alpha2code;
    if (!code) {
      return;
    }
    setSubmitting(true);
    try {
      const updated = await removeJurisdictions(reportId, [code], reason);
      if (updated) {
        onUpdated(updated);
        setRemoving(null);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Group gap="xs" data-testid="report-jurisdictions">
      <Text size="sm" fw={500}>
        Jurisdictions
      </Text>
      {jurisdictions.map((name) => {
        const removable = JURISDICTIONS.some((c) => c.name === name);
        return (
          <Group key={name} gap={2} wrap="nowrap">
            <CountryPill country={name} />
            {removable && (
              <ActionIcon
                size="xs"
                variant="subtle"
                color="gray"
                aria-label={`Remove ${name}`}
                onClick={() => setRemoving(name)}
              >
                <IconX size={12} />
              </ActionIcon>
            )}
          </Group>
        );
      })}
      <BackofficeBehindNotice marker={syncMarker} what="jurisdictions" />
      <RemoveJurisdictionDialog
        opened={removing !== null}
        jurisdictionName={removing ?? ''}
        submitting={submitting}
        onConfirm={confirm}
        onCancel={() => setRemoving(null)}
      />
    </Group>
  );
}
